import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BASE_URL } from "../utils/config";
import villaImage from "../images/Container.svg";
import apartmentImage from "../images/sponer.svg";
import chaletImage from "../images/Tooltip.png";
import editIcon from "../images/bookmark-plus.svg";
import deleteIcon from "../images/xmark-circle.svg";
import acceptIcon from "../images/accept.png";
import { authFetch } from "../utils/authFetch";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import styles from "./MyMatchings.module.css";
import bookmarkFilledIcon from "../images/bookmark-filled.svg";
import NewOffersSwiper from "../components/NewOffersSwiper";
import chevronLeft from "../images/chevron-left-outlined-rounded.svg";
import chevronRight from "../images/chevron-right-outlined-rounded.svg";

const MyMatchings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [matchingProperties, setMatchingProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");
  const [currentPage, setCurrentPage] = useState(1);
  const [bookmarkedPropertyIds, setBookmarkedPropertyIds] = useState(new Set());
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [showConfirmSkipModal, setShowConfirmSkipModal] = useState(false);
  const [matchToSkipId, setMatchToSkipId] = useState(null);
  const [offerPropertyToSkipId, setOfferPropertyToSkipId] = useState(null);
  const [demands, setDemands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDemands = async () => {
      try {
        const response = await authFetch(
          `${BASE_URL}/property/latest/demands?limit=30`,
        );
        if (!response.ok) {
          throw new Error("Failed to fetch demands");
        }

        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          const data = await response.json();
          setDemands(data);
        } else {
          const text = await response.text();
          throw new Error(
            `Server returned a non-JSON response. Check your API route. Response was: "${text.substring(0, 50)}..."`,
          );
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching demands:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDemands();

    // const intervalId = setInterval(fetchDemands, 60000);

    return () => {
      // Polling disabled to prevent property changes during animation
      // clearInterval(intervalId);
    };
  }, []);
  const fetchMatchingProperties = async (page = 1, limit = itemsPerPage, status = activeTab) => {
    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        status: status
      });

      const response = await authFetch(
        `${BASE_URL}/property/matching/all?${params.toString()}`,
        {
          method: "GET",
        },
      );
      if (!response.ok) {
        const errorDetails = await response
          .json()
          .catch(() => ({ message: "Failed to parse error" }));
        throw new Error(
          `Failed to fetch properties: ${response.status} - ${errorDetails.message || response.statusText}`,
        );
      }
      const result = await response.json();
      console.log("API Response for matching properties (FULL):", result);
      const initialBookmarked = new Set();
      const processedData = result.data.map((item) => {
        const property = item.Property;

        console.log(
          `Property ID: ${property.id}, isBookmarked: ${property.isBookmarked}`,
        );

        if (property.isBookmarked) {
          initialBookmarked.add(property.id);
        }

        const address = property.PropertyAddresses[0];
        const showcase = item.PropertyShowcase || property.PropertyShowcase;
        const sqm = property.squareMeters;
        // const sqm = property.squareMeters ? (property.squareMeters * 0.092903).toFixed(0) : '-';
        const formattedPrice = property.price
          ? property.price >= 1000
            ? `${(property.price / 1000).toFixed(0)} K`
            : `${property.price}`
          : "-";
        let propertyTypeAbbr = property.propertyType;
        switch (property.propertyType.toLowerCase()) {
          case "apartment":
            propertyTypeAbbr = "APART.";
            break;
          case "house":
          case "villa":
            propertyTypeAbbr = "H./V.";
            break;
          case "multi-family house":
            propertyTypeAbbr = "MFH";
            break;
          case "plot":
            propertyTypeAbbr = "Plot";
            break;
          default:
            propertyTypeAbbr = property.propertyType;
        }

        const processedItem = {
          id: item.id, // match ID
          offerPropertyId: item.offerPropertyId,
          demandPropertyId: item.demandPropertyId,
          status: item.status,
          matchPercentage: item.matchPercentage,
          displayId: property.displayId || "-",
          zip: address?.zipCode || "-",
          city: address?.city || "-",
          type: propertyTypeAbbr,
          sqm: sqm,
          bedrooms: property.bedrooms || "-",
          units: property.units || "-",
          index: property.index || "-",
          price: formattedPrice,
          accepted: item.status === "accepted",
          mainImage:
            showcase?.mainImage ||
            "https://placehold.co/150x150/E0E0E0/000000?text=No+Image",
          vendorName: showcase?.vendorName || "N/A",
          fullAddress: address?.fullAddress || "",
          propertyDetailId: property.id,
        };
        return processedItem;
      });
      setMatchingProperties(processedData);
      setTotalCount(result.total || 0);
      setBookmarkedPropertyIds(initialBookmarked);
      console.log(
        "Initial Bookmarked Property IDs after fetch:",
        Array.from(initialBookmarked),
      );
    } catch (err) {
      console.error("Failed to fetch matching properties:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatchingProperties(currentPage, itemsPerPage, activeTab);
  }, [currentPage, itemsPerPage, activeTab]);

  const handleSkip = (matchId, offerPropertyId) => {
    console.log(
      "handleSkip called with: matchId =",
      matchId,
      ", offerPropertyId =",
      offerPropertyId,
    );
    if (!matchId || !offerPropertyId) {
      toast.error(t("invalid_skip_data_missing_ids"));
      return;
    }
    setMatchToSkipId(matchId);
    setOfferPropertyToSkipId(offerPropertyId);
    setShowConfirmSkipModal(true);
  };

  const confirmSkip = async () => {
    setShowConfirmSkipModal(false);
    if (!matchToSkipId || !offerPropertyToSkipId) return;

    try {
      toast.info(t("attempting_to_skip_matching"));
      const response = await authFetch(
        `${BASE_URL}/property/matching/updateMatchStatus`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: matchToSkipId,
            offerPropertyId: offerPropertyToSkipId,
            status: "skipped",
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Skip failed:", errorData);
        toast.error(
          t("failed_to_skip_matching", {
            message: errorData.message || response.statusText,
          }),
        );
        return;
      }

      const result = await response.json();
      console.log("Skip success:", result);

      setMatchingProperties((prev) =>
        prev.filter((match) => match.id !== matchToSkipId),
      );
      toast.success(t("matching_skipped_successfully"));
    } catch (error) {
      console.error("Error skipping matching:", error);
      toast.error(t("error_skipping_matching_generic"));
    } finally {
      setMatchToSkipId(null);
      setOfferPropertyToSkipId(null);
    }
  };

  const cancelSkip = () => {
    setShowConfirmSkipModal(false);
    setMatchToSkipId(null);
    setOfferPropertyToSkipId(null);
  };

  const handleToggleBookmark = async (propertyId, currentlyBookmarked) => {
    try {
      let response;
      let message;

      if (currentlyBookmarked) {
        response = await authFetch(
          `${BASE_URL}/bookmark/bookmarks/${propertyId}`,
          {
            method: "DELETE",
          },
        );
        message = t("bookmark_unbookmarked_success");
      } else {
        response = await authFetch(`${BASE_URL}/bookmark/bookmarks`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ propertyId: propertyId }),
        });
        message = t("property_bookmarked_successfully");
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            t(
              currentlyBookmarked
                ? "failed_to_unbookmark_property"
                : "failed_to_bookmark_property",
            ),
        );
      }

      toast.success(message);

      setBookmarkedPropertyIds((prev) => {
        const newSet = new Set(prev);
        if (currentlyBookmarked) {
          newSet.delete(propertyId);
        } else {
          newSet.add(propertyId);
        }
        return newSet;
      });
    } catch (err) {
      console.error("Error toggling bookmark:", err);
      toast.error(t("error_toggling_bookmark", { message: err.message }));
    }
  };

  const handleAccept = async (matchId, offerPropertyId) => {
    console.log(
      "handleAccept called with: matchId =",
      matchId,
      ", offerPropertyId =",
      offerPropertyId,
    );
    if (!matchId || !offerPropertyId) {
      toast.error(t("invalid_accept_data_missing_ids"));
      return;
    }

    try {
      toast.info(t("attempting_to_accept_matching"));
      const response = await authFetch(
        `${BASE_URL}/property/matching/updateMatchStatus`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: matchId,
            offerPropertyId: offerPropertyId,
            status: "accepted",
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Accept failed:", errorData);
        toast.error(
          t("failed_to_accept_matching", {
            message: errorData.message || response.statusText,
          }),
        );
        return;
      }

      const result = await response.json();
      console.log("Accept success:", result);

      // Refetch the current page to update the list with correct status filter
      await fetchMatchingProperties(currentPage, itemsPerPage, activeTab);
      toast.success(t("matching_accepted_successfully"));
    } catch (error) {
      console.error("Error accepting matching:", error);
      toast.error(t("error_accepting_matching_generic"));
    }
  };

  // Backend pagination - no frontend filtering needed
  const currentItems = matchingProperties;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.leftPanel}>
        {!isLoading && !error && demands.length > 0 && (
          <div className={styles.newSearchesHeader}>
            <h2 className={styles.newSearchesTitle}>{t("new_demands")}</h2>
          </div>
        )}
        <div className={styles.propertyList}>
          {demands.length > 0 && (
            <NewOffersSwiper items={demands} className={styles.mySwiper} />
          )}
        </div>
      </div>

      <div className={`${styles.rightPanel}`}>
        <h2 className={styles.pageHeader}>{t("matchings")}</h2>

        <div className={styles.tabsContainer}>
          <button
            className={`${styles.tabButton} ${activeTab === "pending" ? styles.activeTab : styles.inactiveTab}`}
            onClick={() => {
              setActiveTab("pending");
              setCurrentPage(1);
            }}
          >
            {t("pending")}
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === "accepted" ? styles.activeTab : styles.inactiveTab}`}
            onClick={() => {
              setActiveTab("accepted");
              setCurrentPage(1);
            }}
          >
            {t("accepted")}
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '15px', marginTop: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label htmlFor="rowsPerPage">{t("rows_per_page") || "Rows per page"}:</label>
            <select
              id="rowsPerPage"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1); // Reset to first page when changing rows per page
              }}
              style={{
                padding: '5px 10px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '14px'
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        <div className={styles.tableContainer}>
          <table className={styles.searchTable}>
            <thead>
              <tr>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("property_id") || "ID"}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("zip")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("city")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("type")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("Sqm")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("Bedrooms")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("Units")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("Index")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("price")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                    Loading matchings...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                    Error: {error}
                  </td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                    No matching properties found
                  </td>
                </tr>
              ) : currentItems.map((match, index) => {
                console.log("Match object in render loop:", match);

                const navigatePath =
                  activeTab === "pending"
                    ? `/PendingPropertyView/${match.propertyDetailId}`
                    : `/EditOrView/${match.propertyDetailId}`;

                return (
                  <tr
                    key={match.id}
                    className={styles.tableRow}
                    onClick={() => {
                      navigate(navigatePath, {
                        state: {
                          fromMyMatchingsTable: true,
                          propertyStatus: match.status,
                        },
                      });
                    }}
                  >
                    <td className={styles.tableCell} style={{ textAlign: 'center' }}>{match.displayId || "-"}</td>
                    <td className={styles.tableCell} style={{ textAlign: 'center' }}>{match.zip}</td>
                    <td className={styles.tableCell} style={{ textAlign: 'center' }}>{match.city}</td>
                    <td className={styles.tableCell} style={{ textAlign: 'center' }}>{match.type}</td>
                    <td className={styles.tableCell} style={{ textAlign: 'center' }}>
                      {match.sqm || "N/A"} m²
                    </td>
                    <td className={styles.tableCell} style={{ textAlign: 'center' }}>
                      {match.type !== 'multifamily' && match.type !== 'plot'
                        ? (match.bedrooms || '-')
                        : '-'}
                    </td>
                    <td className={styles.tableCell} style={{ textAlign: 'center' }}>
                      {match.type === 'multifamily'
                        ? (match.units || '-')
                        : '-'}
                    </td>
                    <td className={styles.tableCell} style={{ textAlign: 'center' }}>
                      {match.type === 'plot'
                        ? (match.index || '-')
                        : '-'}
                    </td>
                    <td className={styles.tableCell} style={{ textAlign: 'center' }}>{match.price}</td>
                    <td className={`${styles.tableCell} ${styles.whiteBg}`}>
                      <div className={styles.actionButtons}>
                        {activeTab !== "pending" && (
                          <button
                            className={styles.iconButton}
                            title={
                              bookmarkedPropertyIds.has(match.propertyDetailId)
                                ? "Unbookmark Property"
                                : "Bookmark Property"
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleBookmark(
                                match.propertyDetailId,
                                bookmarkedPropertyIds.has(match.propertyDetailId),
                              );
                            }}
                          >
                            <img
                              src={
                                bookmarkedPropertyIds.has(match.propertyDetailId)
                                  ? bookmarkFilledIcon
                                  : editIcon
                              }
                              alt={
                                bookmarkedPropertyIds.has(match.propertyDetailId)
                                  ? "Bookmarked"
                                  : "Bookmark"
                              }
                              className={styles.iconImage}
                            />
                          </button>
                        )}

                        <button
                          className={styles.iconButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log(
                              "Clicking Skip button. match.id:",
                              match.id,
                              "match.offerPropertyId:",
                              match.offerPropertyId,
                            );
                            handleSkip(match.id, match.offerPropertyId);
                          }}
                          title="Skip"
                        >
                          <img
                            src={deleteIcon}
                            alt="Skip"
                            className={styles.iconImage}
                          />
                        </button>
                        {activeTab === "pending" && (
                          <button
                            className={styles.iconButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log(
                                "Clicking Accept button. match.id:",
                                match.id,
                                "match.offerPropertyId:",
                                match.offerPropertyId,
                              );
                              handleAccept(match.id, match.offerPropertyId);
                            }}
                            title="Accept"
                          >
                            <img
                              src={acceptIcon}
                              alt="Accept"
                              className={styles.iconImage}
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className={styles.paginationContainer} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', padding: '10px 0' }}>
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={styles.paginationButton}
              >
                <img src={chevronLeft} alt="Previous" style={{ width: '20px', height: '20px' }} />
              </button>
              {(() => {
                const maxVisible = 5;
                let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                let end = start + maxVisible - 1;
                if (end > totalPages) {
                  end = totalPages;
                  start = Math.max(1, end - maxVisible + 1);
                }
                return Array.from({ length: end - start + 1 }, (_, i) => start + i).map((number) => (
                  <button
                    key={number}
                    onClick={() => paginate(number)}
                    className={`${styles.paginationButton} ${currentPage === number ? styles.activePaginationButton : ""}`}
                  >
                    {number}
                  </button>
                ));
              })()}
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={styles.paginationButton}
              >
                <img src={chevronRight} alt="Next" style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
          )}
        </div>
      </div>
      {showConfirmSkipModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>{t("confirm_skip_title")}</h3>
            <p className={styles.modalMessage}>{t("confirm_skip_message")}</p>
            <div className={styles.modalButtons}>
              <button
                className={`${styles.modalButton} ${styles.modalButtonConfirm}`}
                onClick={confirmSkip}
              >
                {t("yes_skip_button")}
              </button>
              <button
                className={`${styles.modalButton} ${styles.modalButtonCancel}`}
                onClick={cancelSkip}
              >
                {t("cancel_button")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyMatchings;
